import { PlayCircleOutline } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

export type ChatMessageRole = 'user' | 'assistant';

export interface ChatMessageBubbleProps {
  content: string;
  createdAt: string;
  role: ChatMessageRole;
}

export default function ChatMessageBubble(
  props: ChatMessageBubbleProps,
): React.ReactElement {
  const isUser = props.role === 'user';
  const sender = isUser ? 'You' : 'Jeff';

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        px: 4,
        py: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          position: 'relative',
          bgcolor: isUser ? '#AFAFAF' : 'rgba(255, 212, 0, 0.65)',
          borderRadius: '25px',
          filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
          px: 2.25,
          py: 1.75,
          color: '#000000',
          '&:after': {
            content: '""',
            position: 'absolute',
            width: 18,
            height: 18,
            bottom: -14,
            borderRadius: '50%',
            bgcolor: isUser ? '#AFAFAF' : '#FFD400',
            right: isUser ? -14 : 'auto',
            left: isUser ? 'auto' : -14,
          },
        }}
      >
        {isUser ? (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {props.content}
          </Typography>
        ) : (
          <Box
            sx={{
              '& p': { margin: 0, marginBottom: 1 },
              '& p:last-child': { marginBottom: 0 },
              '& strong': { fontWeight: 'bold' },
              '& em': { fontStyle: 'italic' },
              '& ul, & ol': { marginLeft: 2, marginBottom: 1 },
              '& li': { marginBottom: 0.5 },
            }}
          >
            <ReactMarkdown
              components={{
                // Reason: render /content/ links as React Router Links
                // so they navigate in-app instead of full page reload
                a: ({ href, children }) => {
                  if (href && href.startsWith('/content/')) {
                    return (
                      <Link
                        to={href}
                        style={{
                          color: '#333',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          background: 'rgba(255,212,0,0.35)',
                          borderRadius: 8,
                          padding: '2px 8px',
                          fontWeight: 600,
                        }}
                      >
                        <PlayCircleOutline sx={{ fontSize: 16 }} />
                        {children}
                      </Link>
                    );
                  }
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1a73e8', textDecoration: 'underline' }}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {props.content}
            </ReactMarkdown>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            opacity: 0.7,
          }}
        >
          {sender} {format(new Date(props.createdAt), 'HH:mm')}
        </Typography>
      </Box>
    </Box>
  );
}
